import React from 'react';
import PropTypes from 'prop-types';

import styles from './styles/styles.module.scss';
import formStyles from '../../styles/styles.module.scss';
import baseStyles from '../../../../styles/baseStyles.module.scss';
import { ProPageStrings, StringConstantsMixin } from '../../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { validatePaymentForm, getTotalCost, getTotalBaseAnnualCost } from '../../../../utils/form_validaters/payment_form_helper';
import { hasErrors } from '../../../../utils/form_validaters/user_form_helper';
import { ServerCommunicationUtil } from '../../../../utils/server_communication_util';
import { LanguageSupportUtil } from '../../../../utils/language_support_util';
import { loadRecaptcha, executeRecaptcha } from '../../../../utils/recaptcha_helpers';

import { PLAN_ANNUAL, PLAN_THREE_YEAR, PLAN_MONTHLY } from '../../../../pages/ProPage';
import PlanTypeCard from '../PlanTypeCard';
import PlanTypeCardCollection from '../PlanTypeCardCollection';
import IconTabBar from '../../../shared/IconTabBar';

import Wrapper from '../../Wrapper';
import CreditCardForm from '../../CreditCardForm/CreditCardForm';
import ExternalPayForm from '../../ExternalPayForm/ExternalPayForm';
import CouponCode from '../../CouponCode';

import FormButton from '../../FormButton';
import { trackOptimizelyEvent, OptimizelyConstants } from '../../../../utils/optimizely_helpers';

const log = item => {
  console.log('[OPTIMIZELY]', item);
};

class PaymentForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: {
        name: '',
        number: '',
        cvv: '',
        'cc-exp-month': '',
        'cc-exp-year': '',
        postalCode: ''
      },
      name: '',
      number: '',
      cvv: '',
      'cc-exp-month': null,
      'cc-exp-year': null,
      postalCode: '',
      couponError: '',
      serverError: null,
      maxCodeSize: 3,
      isCouponInputExpanded: false,
      submitting: false,
      paypalToken: null,
      paypal: null,
      applePay: null,
      amazonPayToken: null,
      applePayToken: null,
      activePaymentTab: 0,
      editingPlanType: false,
      disableSubmit: false,
      recaptchaObject: null
    };
  }

  static TEXT = { ...ProPageStrings.paymentText, creditCardForm: ProPageStrings.creditCardForm };

  static SUCCESS_URL = '/pro/welcome';

  static THREE_YEAR_PLAN = 'threeYr';

  static GIFT_CARD = 'GiftCard';

  componentDidMount() {
    if (this.props.paymentTypes[1].showTab && typeof recurly !== 'undefined') {
      recurly.configure(this.props.recurlyPublicKey);
      this.setupThirdPartyPayments();
    }

    if (this.props.enableRecaptcha && !this.state.recaptchaObject) {
      loadRecaptcha(recaptchaObject => {
        this.setState({ disableSubmit: false, recaptchaObject });
      });
    }
  }

  // setups
  setupThirdPartyPayments() {
    this.setupApplePay();
    const planType = this.getActivePlanType();
    const paypal = recurly.PayPal({
      display: {
        displayName: 'AllTrails Pro',
        amount: planType.priceTotal
      }
    });
    paypal.on('error', err => {
      console.error(err);
    });
    paypal.on('token', token => {
      this.setState({ paypalToken: token.id }, this.loadRecaptchaAndSubmit);
    });

    this.setState({ paypal });
  }

  setupApplePay() {
    let applePay;
    const planType = this.getActivePlanType();
    if (this.props.paymentTypes[2].showTab) {
      let priceTotal = planType.priceTotal;
      if (this.props.verifiedCoupon && this.props.verifiedCoupon.discountedPricePerYear) {
        priceTotal = this.props.verifiedCoupon.discountedPricePerYear;
      }
      try {
        applePay = recurly.ApplePay({
          country: 'US', // this is the merchant's country code, so will remain US
          currency: this.props.currencyCode,
          label: 'AllTrails Pro',
          total: priceTotal
        });
        applePay.on('error', err => {
          console.error('Apple Pay error', err);
        });
        applePay.on('token', token => {
          // Submit the token to server when flow is completed successfully
          this.setState({ applePayToken: token.id }, this.loadRecaptchaAndSubmit);
        });
      } catch (error) {
        console.error(error);
      }
    }

    this.setState({ applePay });
  }

  // handlers
  handlePaymentTabSwitch = activePaymentTab => {
    this.setState({ activePaymentTab });
  };

  handleCreditCardFormChange = event => {
    const { name, value } = event.target;
    const monthAndYear = this.getMonthAndYear();
    const payload = validatePaymentForm(name, value, monthAndYear, this.state.errors, this.props.text.creditCardForm, this.state.maxCodeSize);
    this.setState({ ...payload });
  };

  handleApplePayClick = event => {
    if (!this.state.applePay) {
      return;
    }

    this.state.applePay.begin();
  };

  handlePaypalClick = event => {
    if (!this.state.paypal) {
      return;
    }

    this.state.paypal.start();
  };

  handleAmazonClick = amazonPayToken => {
    this.setState({ amazonPayToken }, this.loadRecaptchaAndSubmit);
  };

  trackConversionInTatari() {
    // Safety first - Wrap anything Tatari-related in a try/catch
    try {
      const activePlan = this.getActivePlanType();
      if (window.tatari) {
        const plan = activePlan.recurlyPlanCode;
        const total = activePlan.priceTotal;
        window.tatari.track('purchase', { plan, total });
      }
    } catch (error) {
      console.error('Error tracking conversion in Tatari', error);
    }
  }

  handlePaymentFormSuccess = data => {
    const { optimizelyInstance, optimizelyUserId, locale, activePlanType, optimizelyAttributeData } = this.props;

    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'submit']);

    this.trackConversionInTatari();
    if (this.props.optimizelyInstance) {
      // Track any pro conversion event, outside of any current tests
      trackOptimizelyEvent(
        optimizelyInstance,
        optimizelyUserId,
        OptimizelyConstants.EVENTS.PRO_CONVERSION,
        OptimizelyConstants.OUTCOMES.SUCCESS,
        optimizelyAttributeData
      );

      const eventName = {
        [PLAN_MONTHLY]: OptimizelyConstants.EVENTS.PRO_CONVERSION_MONTHLY,
        [PLAN_ANNUAL]: OptimizelyConstants.EVENTS.PRO_CONVERSION_ANNUAL,
        [PLAN_THREE_YEAR]: OptimizelyConstants.EVENTS.PRO_CONVERSION_3_YEAR
      }[activePlanType];
      trackOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventName);
    }
    setTimeout(() => {
      window.location.href = LanguageSupportUtil.wrapUrlSafe(PaymentForm.SUCCESS_URL, locale);
    }, 200); // small delay to let Optimizely event go through
  };

  handlePaymentFormError = err => {
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'payment_error']);
    let { errors, serverError } = this.state;

    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText);
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        serverError = errorObj.base[0];
      }
      if (errorObj.cc_number) {
        errors.number = errorObj.cc_number[0];
      }
      if (errorObj.cc_expiration_month) {
        errors['cc-exp-month'] = errorObj.cc_expiration_month[0];
      }
      if (errorObj.cc_expiration_year) {
        errors['cc-exp-year'] = errorObj.cc_expiration_year[0];
      }
      if (errorObj.cc_csc) {
        errors.cvv = errorObj.cc_csc[0];
      }
      if (errorObj.first_name || errorObj.last_name) {
        const nameErrors = [];
        if (errorObj.first_name) {
          nameErrors.push(`${StringConstantsMixin.strings.SIGNUP.FIRST_NAME}: ${errorObj.first_name[0]}`);
        }
        if (errorObj.last_name) {
          nameErrors.push(`${StringConstantsMixin.strings.SIGNUP.LAST_NAME}: ${errorObj.last_name[0]}`);
        }
        errors.name = nameErrors.join(', ');
      }
    } else {
      serverError = this.props.text.serverError;
    }

    this.setState({ errors, serverError, submitting: false });
  };

  // gets
  getMonthAndYear() {
    return {
      'cc-exp-month': this.state['cc-exp-month'],
      'cc-exp-year': this.state['cc-exp-year']
    };
  }

  getCouponCodeInput() {
    return (
      <CouponCode
        text={this.props.text}
        expanded={this.props.isCouponInputExpanded}
        removeHandler={this.props.handleRemoveCoupon}
        showHandler={this.props.handleShowCouponCodeInput}
        changeHandler={this.props.handleCouponCodeChange}
        applyHandler={this.props.handleApplycoupon}
        isSuccess={this.props.isCouponApplied}
        isError={this.props.couponError}
        errorMessage={this.props.couponError}
        value={this.props.couponCode}
      />
    );
  }

  clearErrors() {
    this.setState({
      errors: {
        name: '',
        number: '',
        cvv: '',
        'cc-exp-month': '',
        'cc-exp-year': '',
        postalCode: ''
      }
    });
  }

  getFootText() {
    const { footText } = this.props.text;

    if (!footText) {
      return '';
    }

    const currentPlan = this.getActivePlanType();
    const totalCost = getTotalCost(this.props.planTypes, this.props.activePlanType, this.props.verifiedCoupon, this.props.currencyCode);
    const baseAnnualCost = getTotalBaseAnnualCost(this.props.planTypes, this.props.activePlanType, this.props.currencyCode);
    const months = currentPlan.numberOfMonths;

    if (this.props.activePlanType === PaymentForm.THREE_YEAR_PLAN) {
      return this.props.text.threeYearFootText;
    }

    const tail = months === '1' ? footText.tailSingular : footText.tail;

    return this.props.isCouponApplied
      ? `${footText.lead} ${totalCost} ${footText.mid} ${months} ${footText.discountMid} ${baseAnnualCost} ${footText.discountTail}`
      : `${footText.lead} ${totalCost} ${footText.mid} ${months} ${tail}`;
  }

  getFirstAndLastName() {
    const fullName = this.state.name.split(' ');
    const billing_first_name = fullName[0];
    const billing_last_name = fullName[fullName.length - 1];

    return { billing_first_name, billing_last_name };
  }

  // validators

  noGiftCard() {
    if (!this.props.verifiedCoupon) {
      return true;
    }

    return this.props.verifiedCoupon.type !== PaymentForm.GIFT_CARD;
  }

  noAlternatePayment() {
    return this.state.applePayToken === null && this.state.amazonPayToken === null && this.state.paypalToken === null;
  }

  failedFormValidation() {
    let validation;
    let validationErrors = { ...this.state.errors };
    const monthAndYear = this.getMonthAndYear();

    for (const name in this.state.errors) {
      validation = validatePaymentForm(
        name,
        this.state[name],
        monthAndYear,
        this.state.errors,
        this.props.text.creditCardForm,
        this.state.maxCodeSize
      );
      validationErrors = { ...validationErrors, ...validation.errors };
    }
    this.setState({ errors: validationErrors });
    return hasErrors(validationErrors);
  }

  loadRecaptchaAndSubmit = e => {
    if (e) {
      e.preventDefault();
    }

    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true }, () => ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'submit']));
    if (this.noAlternatePayment() && this.noGiftCard()) {
      if (this.failedFormValidation()) {
        this.setState({ submitting: false });
        return;
      }
    }

    if (this.props.enableRecaptcha) {
      executeRecaptcha(this.state.recaptchaObject, 'userPaymentAction').then(this.submitPayment);
    } else {
      this.submitPayment();
    }
  };

  submitPayment = (recaptchaToken = null) => {
    const { billing_first_name, billing_last_name } = this.getFirstAndLastName();
    const submissionData = {
      plan: this.props.activePlanType,
      recurly_plan_code: this.props.recurlyPlanCode,
      force_monthly_price_point: this.props.forceMonthlyPricePoint,
      paypal_token: this.state.paypalToken,
      apple_pay_token: this.state.applePayToken,
      amazon_pay_token: this.state.amazonPayToken,
      recaptcha: recaptchaToken,
      user: {
        user_entered_coupon_code: this.props.verifiedCoupon ? this.props.verifiedCoupon.code : null,
        cc_number: this.state.number,
        cc_expiration_month: this.state['cc-exp-month'] ? this.state['cc-exp-month'].value : null,
        cc_expiration_year: this.state['cc-exp-year'] ? this.state['cc-exp-year'].value : null,
        cc_csc: this.state.cvv,
        zip: this.state.postalCode,
        billing_first_name,
        billing_last_name
      }
    };

    if (this.props.handlePayment) {
      this.props.handlePayment(submissionData).catch(err => {
        this.handlePaymentFormError(err);
      });

      return;
    }

    ServerCommunicationUtil.putApiEndpoint('/pro', submissionData, this.handlePaymentFormSuccess, this.handlePaymentFormError, () => {}, {
      'X-Language-Locale': this.props.locale
    });
  };

  // renders
  renderServerError() {
    return <div className={this.state.serverError ? styles.serverError : ''}>{this.state.serverError}</div>;
  }

  renderExternalPayForm() {
    return (
      <div>
        <div>
          <div className={formStyles.promptHead}>{this.props.text.chooseMethodHead}</div>
          <div className={formStyles.prompt}>{this.props.text.chooseMethodPrompt}</div>
        </div>
        {this.props.isCouponInputShown ? this.getCouponCodeInput() : ''}
        <ExternalPayForm
          text={this.props.text}
          paypalHandler={this.handlePaypalClick}
          applePayHandler={this.handleApplePayClick}
          amazonPayHandler={this.handleAmazonClick}
          showApplePay={this.props.paymentTypes[2].showTab}
          showAmazonPay={this.props.paymentTypes[3].showTab}
          amazonSellerId={this.props.amazonSellerId}
          amazonClientId={this.props.amazonClientId}
        />
        {this.renderServerError()}
      </div>
    );
  }

  renderCreditCardForm() {
    return (
      <form onSubmit={this.loadRecaptchaAndSubmit}>
        <CreditCardForm
          text={this.props.text.creditCardForm}
          errors={this.state.errors}
          changeHandler={this.handleCreditCardFormChange}
          name={this.state.name}
          number={this.state.number}
          month={this.state['cc-exp-month']}
          year={this.state['cc-exp-year']}
          cvv={this.state.cvv}
          postalCode={this.state.postalCode}
          submitHandler={this.loadRecaptchaAndSubmit}
        />
        {this.props.isCouponInputShown ? this.getCouponCodeInput() : ''}
        {this.renderServerError()}
        <div className={styles.submit}>
          <FormButton value={this.props.text.submitText} disabled={this.state.submitting || this.state.disableSubmit} />
        </div>
      </form>
    );
  }

  renderPaymentFormBody() {
    return this.state.activePaymentTab === 0 ? this.renderCreditCardForm() : this.renderExternalPayForm();
  }

  editPlanType = () => {
    this.setState({ editingPlanType: !this.state.editingPlanType });
  };

  handleTypeClick = (activePlanType, recurlyPlanCode) => {
    this.editPlanType();
    this.props.updateActivePlan(activePlanType, recurlyPlanCode);
  };

  getActivePlanType() {
    return this.props.planTypes.find(planType => planType.planCode === this.props.activePlanType);
  }

  getPlanTypeCard() {
    if (this.state.editingPlanType) {
      return (
        <div className={styles.planTypeCardCollection}>
          <PlanTypeCardCollection
            planTypes={this.props.planTypes}
            handler={this.handleTypeClick}
            activePlanType={this.props.activePlanType}
            currencyCode={this.props.currencyCode}
            verifiedCoupon={this.state.verifiedCoupon}
            isCouponApplied={this.state.isCouponApplied}
            isSuccess={this.state.isCouponApplied}
          />
        </div>
      );
    }

    const activePlan = this.getActivePlanType();

    return (
      <PlanTypeCard
        staticCard
        option={activePlan}
        currencyCode={this.props.currencyCode}
        verifiedCoupon={this.props.verifiedCoupon}
        isCouponApplied={this.props.isCouponApplied}
      />
    );
  }

  render() {
    const footText = this.getFootText();

    return (
      <Wrapper text={this.props.text} foot={footText}>
        <div className={baseStyles.section}>
          <div className={baseStyles.row}>
            {this.getPlanTypeCard()}
            <div className={`${styles.prompt} ${baseStyles.link} `} onClick={this.editPlanType}>
              {this.props.text.changePlanText}
            </div>
          </div>
          <div className={baseStyles.row}>
            <IconTabBar
              text={this.props.text}
              tabs={this.props.paymentTypes}
              handler={this.handlePaymentTabSwitch}
              activeTab={this.state.activePaymentTab}
            />
          </div>
          <div className={`${formStyles.body} ${styles.body}`}>{this.renderPaymentFormBody()}</div>
        </div>
      </Wrapper>
    );
  }
}

PaymentForm.defaultProps = {
  changePlanText: 'Change plan'
};

PaymentForm.propTypes = {
  activePlanType: PropTypes.number,
  recurlyPlanCode: PropTypes.string,
  amazonClientId: PropTypes.string,
  changePlanText: PropTypes.string,
  couponError: PropTypes.string,
  currencyCode: PropTypes.string,
  goBack: PropTypes.func,
  handleApplyCoupon: PropTypes.func,
  handleCouponCodeChange: PropTypes.func,
  handleRemoveCoupon: PropTypes.func,
  handleShowCouponCodeInput: PropTypes.func,
  isCouponApplied: PropTypes.bool,
  isCouponInputExpanded: PropTypes.bool,
  isCouponInputShown: PropTypes.bool,
  handlePayment: PropTypes.func,
  paymentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      tab: PropTypes.number,
      showIcon: PropTypes.bool,
      iconPath: PropTypes.string
    })
  ),
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      planCode: PropTypes.string,
      priceMonth: PropTypes.number,
      priceTotal: PropTypes.priceTotal,
      numberOfMonths: PropTypes.string,
      showDiscount: PropTypes.bool
    })
  ),
  recurlyPublicKey: PropTypes.string,
  verifiedCoupon: PropTypes.shape({
    discountPercentage: PropTypes.number
  }),
  optimizelyInstance: PropTypes.object,
  optimizelyUserId: PropTypes.string
};

PaymentForm.defaultProps = {
  handlePayment: null
};

export default PaymentForm;
